/**
 * The ledger, and its Live slice.
 *
 * A bet is a permanent financial record, so every match detail is denormalised onto the row: it
 * stays readable even after the match is pruned or its prediction re-run. The signal it was taken
 * on is snapshotted too, so a later re-run cannot rewrite why it was placed.
 *
 * One screen, two filters. Bets is everything, newest first; Live is the bets on matches in
 * progress right now (kickoff ≤ now ≤ kickoff + 3h), soonest first. Two screen files with the same
 * state would be a fork, not adaptivity.
 */
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import {
  betEdge,
  isLiveBet,
  kickoffDisplay,
  money,
  pct,
  potentialPayout,
  realisedProfit,
  signed,
  type PlacedBet,
} from '@/models';
import { Empty } from '@/screens/Upcoming';
import { useStore } from '@/store';
import * as React from 'react';
import { ScrollView, View } from 'react-native';

export function BetsScreen({ live = false }: { live?: boolean }) {
  const { bets } = useStore();

  const shown = React.useMemo(() => {
    if (!live) return bets;
    return bets
      .filter((b) => isLiveBet(b))
      .sort((a, b) => Date.parse(a.kickoffUtc) - Date.parse(b.kickoffUtc));
  }, [bets, live]);

  return (
    <ScrollView contentContainerClassName="gap-3 pb-6">
      <Summary bets={shown} />

      {shown.length === 0 ? (
        <Empty
          title={live ? 'Nothing in play' : 'No bets yet'}
          body={
            live
              ? 'Bets appear here while their match is running — from kickoff until three hours after.'
              : 'Set a stake on Upcoming and press Place. The PC prices it against the live book and records the fill here.'
          }
        />
      ) : (
        shown.map((b) => <BetRow key={b.id} bet={b} />)
      )}
    </ScrollView>
  );
}

function Summary({ bets }: { bets: PlacedBet[] }) {
  if (bets.length === 0) return null;

  const staked = bets.reduce((a, b) => a + b.cost, 0);
  const payout = bets.reduce((a, b) => a + potentialPayout(b), 0);
  const settled = bets.filter((b) => realisedProfit(b) !== null);
  const pnl = settled.reduce((a, b) => a + (realisedProfit(b) ?? 0), 0);
  const won = bets.filter((b) => b.outcome === 'Won').length;
  const lost = bets.filter((b) => b.outcome === 'Lost').length;
  const pending = bets.filter((b) => b.outcome === 'Pending').length;
  const edges = bets.map(betEdge).filter((e): e is number => e !== null);

  return (
    <View className="gap-2">
      <View className="flex-row flex-wrap gap-2">
        <Stat label="Bets" value={String(bets.length)} />
        <Stat label="Staked" value={money(staked)} />
        <Stat label="Max payout" value={money(payout)} />
        <Stat
          label="Settled P&L"
          value={`${pnl >= 0 ? '+' : '−'}${money(Math.abs(pnl))}`}
          tone={settled.length === 0 ? undefined : pnl >= 0 ? 'good' : 'bad'}
        />
      </View>
      <Text variant="small" className="text-muted-foreground">
        {won}W-{lost}L, {pending} pending
        {edges.length ? ` · avg edge ${signed(edges.reduce((a, e) => a + e, 0) / edges.length, 1)} pts` : ''}
      </Text>
    </View>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'bad' }) {
  return (
    <View className="border-border bg-card min-w-[110px] flex-1 rounded-lg border p-3">
      <Text variant="caption" className="text-muted-foreground uppercase">
        {label}
      </Text>
      <Text
        variant="heading"
        className={cn(tone === 'good' && 'text-success', tone === 'bad' && 'text-destructive')}>
        {value}
      </Text>
    </View>
  );
}

function BetRow({ bet }: { bet: PlacedBet }) {
  const e = betEdge(bet);
  const realised = realisedProfit(bet);

  return (
    <Card>
      <CardContent className="gap-1 py-3">
        <View className="flex-row items-start justify-between gap-3">
          <View className="min-w-0 flex-1">
            <Text variant="heading" numberOfLines={1}>
              {bet.betName}
            </Text>
            <Text variant="small" className="text-muted-foreground" numberOfLines={1}>
              {bet.homeTeam} v {bet.awayTeam} · {bet.league}
            </Text>
          </View>
          <Badge
            variant={
              bet.outcome === 'Won'
                ? 'success'
                : bet.outcome === 'Lost'
                  ? 'destructive'
                  : isLiveBet(bet)
                    ? 'default'
                    : 'secondary'
            }>
            <Text>{bet.outcome === 'Pending' && isLiveBet(bet) ? 'In play' : bet.outcome}</Text>
          </Badge>
        </View>

        <Text variant="small" className="text-muted-foreground">
          {bet.shares.toFixed(2)} sh @ ${bet.avgPrice.toFixed(3)} = {money(bet.cost)}
          {bet.isPartial ? '  (partial — slippage cap bit)' : ''}
        </Text>

        <Text variant="small" className="text-muted-foreground">
          AI {pct(bet.aiPercent)} vs market {pct(bet.marketPercent)}
          {e !== null ? `  ·  edge ${signed(e)} pts` : ''}
          {bet.modelLeague ? `  ·  ${bet.modelLeague} · ${bet.modelResults} res` : ''}
        </Text>

        <View className="flex-row items-baseline justify-between gap-2">
          <Text variant="caption" className="text-muted-foreground">
            {kickoffDisplay(bet.kickoffUtc)} · {bet.status}
          </Text>
          <Text
            variant="small"
            className={cn(
              realised === null
                ? 'text-muted-foreground'
                : realised >= 0
                  ? 'text-success'
                  : 'text-destructive',
            )}>
            {realised === null
              ? `→ ${money(potentialPayout(bet))}`
              : `${realised >= 0 ? '+' : '−'}${money(Math.abs(realised))}`}
          </Text>
        </View>
      </CardContent>
    </Card>
  );
}
